import { css } from "dx-styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, PENDING, api } from "../api/client";
import { button, card, heading, layout, mono, muted, stack, subheading } from "../components/primitives";
import { countdown } from "../lib/format";
import { qrSvg } from "../lib/qr";
import { tokens } from "../theme/tokens";

const split = css({
  display: "grid",
  gridTemplateColumns: "minmax(240px, 320px) 1fr",
  gap: tokens.space.xl,
  alignItems: "start",
  "@media (max-width: 720px)": { gridTemplateColumns: "1fr" },
});

const qrFrame = css({
  display: "grid",
  placeItems: "center",
  padding: tokens.space.md,
  backgroundColor: tokens.color.paper,
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: tokens.radius.md,
  "& svg": { width: "100%", height: "auto", display: "block" },
});

const codeText = css({
  fontSize: "26px",
  fontWeight: 700,
  letterSpacing: "0.12em",
});

const steps = css({
  margin: 0,
  paddingInlineStart: tokens.space.lg,
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.sm,
});

/** How the phone's camera reads it, and what the user types if it does not. */
function grouped(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

type State =
  | { readonly kind: "requesting" }
  | { readonly kind: "waiting"; readonly code: string; readonly expiresAt: number; readonly svg: string }
  | { readonly kind: "expired" }
  | { readonly kind: "failed"; readonly message: string };

export function Login() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "requesting" });
  const [secondsLeft, setSecondsLeft] = useState(0);
  // A stale poll from a previous code must not resurrect it.
  const generation = useRef(0);
  /// The provisional cookie is a single slot: two pairing requests in flight
  /// means the cookie can end up belonging to the code we are *not* polling, and
  /// the poll answers 403. React's development double-invoke found this.
  const inFlight = useRef(false);

  const requestCode = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const mine = ++generation.current;
    setState({ kind: "requesting" });
    try {
      const pairing = await api.requestPairingCode();
      const svg = await qrSvg(`diditakeit://authorize?code=${pairing.code}`);
      if (generation.current !== mine) return;
      setState({
        kind: "waiting",
        code: pairing.code,
        expiresAt: new Date(pairing.expiresAt).getTime(),
        svg,
      });
    } catch (error) {
      if (generation.current !== mine) return;
      setState({
        kind: "failed",
        message: error instanceof ApiError ? error.message : "Could not reach the service.",
      });
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void requestCode();
  }, [requestCode]);

  // 1 Hz, as specified: the code lives for two minutes, so this is 120 requests
  // and the server rate-limits the attempt rather than the address.
  useEffect(() => {
    if (state.kind !== "waiting") return;
    const mine = generation.current;
    const timer = window.setInterval(() => {
      setSecondsLeft(Math.round((state.expiresAt - Date.now()) / 1000));
      void (async () => {
        try {
          const result = await api.pollPairing(state.code);
          if (generation.current !== mine) return;
          if (result !== PENDING) navigate("/", { replace: true });
        } catch (error) {
          if (generation.current !== mine) return;
          if (error instanceof ApiError && (error.status === 410 || error.status === 403)) {
            setState({ kind: "expired" });
          }
          // Anything else is transient — a dropped request should not throw the
          // user out of a flow that is still valid.
        }
      })();
    }, 1000);
    return () => window.clearInterval(timer);
  }, [state, navigate]);

  return (
    <main className={layout}>
      <header className={stack}>
        <h1 className={heading}>Template authoring for Did I Take It?</h1>
        <p className={muted}>
          Scan with your phone camera. The app opens and asks you to confirm this browser — no password, no
          email.
        </p>
      </header>

      <div className={split}>
        <section className={card} aria-live="polite">
          {state.kind === "waiting" ? (
            <div className={stack}>
              <div
                className={qrFrame}
                // The SVG comes from the QR encoder in this bundle, not from the
                // network or from user input.
                dangerouslySetInnerHTML={{ __html: state.svg }}
                role="img"
                aria-label={`QR code for sign-in code ${grouped(state.code)}`}
              />
              <div className={stack}>
                <span className={muted}>or type</span>
                <span className={`${codeText} ${mono}`}>{grouped(state.code)}</span>
                <span className={muted}>
                  Expires in {countdown(secondsLeft)} ·{" "}
                  <button type="button" className={button({ size: "sm" })} onClick={() => void requestCode()}>
                    New code
                  </button>
                </span>
              </div>
            </div>
          ) : state.kind === "requesting" ? (
            <p className={muted}>Asking for a code…</p>
          ) : state.kind === "expired" ? (
            <div className={stack}>
              <p>That code expired.</p>
              <button type="button" className={button({ tone: "primary" })} onClick={() => void requestCode()}>
                Get a new code
              </button>
            </div>
          ) : (
            <div className={stack}>
              <p>{state.message}</p>
              <button type="button" className={button({ tone: "primary" })} onClick={() => void requestCode()}>
                Try again
              </button>
            </div>
          )}
        </section>

        <section className={stack}>
          <h2 className={subheading}>What happens next</h2>
          <ol className={steps}>
            <li>The app shows which browser, device and address asked to sign in.</li>
            <li>
              You tap <strong>Authorize this browser</strong> — or decline, and nothing is sent.
            </li>
            <li>This page signs in on its own.</li>
          </ol>
          <p className={muted}>
            Don&rsquo;t have the app yet? Collections are authored here and read on the phone.
          </p>
        </section>
      </div>
    </main>
  );
}
